import { createBrowserRouter, Navigate, useLocation } from 'react-router-dom';
import { lazy, Suspense, ComponentType, LazyExoticComponent } from 'react';
import AppShell from './layout/AppShell';
import ProtectedRoute from './layout/ProtectedRoute';
import { BootSpinner, PageLoading } from './components/PageLoading';
import ConnectDone from './pages/ConnectDone';
import { INBOUND_FLOWS_ENABLED, VOICE_NOTES_ENABLED } from './config/featureFlags';

/**
 * Wrapper for React.lazy that handles chunk load failures gracefully.
 * On failure: retries once after 1.5s delay, then forces page reload.
 * Prevents infinite reload loops using sessionStorage.
 */
function lazyWithRetry<T extends ComponentType<any>>(
  importFn: () => Promise<{ default: T }>
): LazyExoticComponent<T> {
  return lazy(() =>
    importFn().catch((error: Error) => {
      // Check if it's a chunk load error
      const isChunkError =
        error.message?.includes('dynamically imported') ||
        error.message?.includes('Failed to fetch') ||
        error.message?.includes('Loading chunk') ||
        error.message?.includes('ChunkLoadError') ||
        error.name === 'ChunkLoadError';

      if (isChunkError) {
        console.warn('[CHUNK] Load failed, retrying in 1.5s...', error.message);

        // Wait 1.5 seconds then retry
        return new Promise<{ default: T }>((resolve, reject) => {
          setTimeout(() => {
            importFn()
              .then(resolve)
              .catch((retryError: Error) => {
                console.error('[CHUNK] Retry failed, triggering reload...', retryError.message);

                // Prevent infinite reload loop using sessionStorage
                const reloadKey = `chunk_reload_${window.location.pathname}`;
                const lastReload = sessionStorage.getItem(reloadKey);
                const now = Date.now();

                if (!lastReload || now - parseInt(lastReload) > 10000) {
                  // More than 10 seconds since last reload, safe to reload
                  sessionStorage.setItem(reloadKey, now.toString());
                  window.location.reload();
                }

                // If we can't reload (recently reloaded), reject with original error
                // ErrorBoundary will catch this and show user-friendly UI
                reject(retryError);
              });
          }, 1500);
        });
      }

      // Not a chunk error, rethrow
      throw error;
    })
  );
}

// Lazy load all pages with retry logic for chunk errors
const Hero = lazyWithRetry(() => import('./pages/Hero'));
const HomeChat = lazyWithRetry(() => import('./pages/HomeChat'));
const Launchpad = lazyWithRetry(() => import('./pages/Launchpad'));
const WorkspaceLaunchpad = lazyWithRetry(() => import('./pages/WorkspaceLaunchpad'));
// Dashboard removed - redirects to Launchpad
// PersonaPrompting removed - AI Calling Campaigns merged into Quick Call
const Contacts = lazyWithRetry(() => import('./pages/Contacts'));
const Campaigns = lazyWithRetry(() => import('./pages/Sequences')); // Still using Sequences file but renamed component
const PhoneNumbers = lazyWithRetry(() => import('./pages/PhoneNumbers'));
const LeadData = lazyWithRetry(() => import('./pages/LeadData'));
const Appointments = lazyWithRetry(() => import('./pages/Appointments'));
// ATS / Pipeline / CRM pages deprecated — routes Navigate to /app (see below)
const Admin = lazyWithRetry(() => import('./pages/Admin'));
const Signals = lazyWithRetry(() => import('./pages/Signals'));
const Login = lazyWithRetry(() => import('./pages/Login'));
const Billing = lazyWithRetry(() => import('./pages/Billing'));
const BillingSuccess = lazyWithRetry(() => import('./pages/BillingSuccess'));
const PaidSetup = lazyWithRetry(() => import('./pages/PaidSetup'));
const BillingCancel = lazyWithRetry(() => import('./pages/BillingCancel'));
const SignUp = lazyWithRetry(() => import('./pages/SignUp'));
const EmailTemplates = lazyWithRetry(() => import('./components/EmailTemplates'));
const SMSTemplates = lazyWithRetry(() => import('./components/SMSTemplates'));
const Knowledge = lazyWithRetry(() => import('./pages/Knowledge'));
const QuickCall = lazyWithRetry(() => import('./pages/QuickCall'));
const LinkedInSettings = lazyWithRetry(() => import('./pages/LinkedInSettings'));
const ReplyInbox = lazyWithRetry(() => import('./pages/ReplyInbox'));
// Sending infrastructure (mailboxes + domains + warmup) — replaces the
// legacy EmailAccountsWarmup page. The legacy file is kept for reference and
// can be re-imported here as a fallback if we ever need to roll back.
const SendingInfrastructure = lazyWithRetry(() => import('./pages/SendingInfrastructure'));
const LegalPrivacy = lazyWithRetry(() => import('./pages/LegalPrivacy'));
const LegalTerms = lazyWithRetry(() => import('./pages/LegalTerms'));
const LegalDPA = lazyWithRetry(() => import('./pages/LegalDPA'));
const LegalSubprocessors = lazyWithRetry(() => import('./pages/LegalSubprocessors'));
const LegalCookies = lazyWithRetry(() => import('./pages/LegalCookies'));
const UnsubscribeBridge = lazyWithRetry(() => import('./pages/UnsubscribeBridge'));
// Documentation Pages
const DocsHub = lazyWithRetry(() => import('./pages/DocsHub'));
const DocsConsent = lazyWithRetry(() => import('./pages/DocsConsent'));
const ConversationModels = lazyWithRetry(() => import('./pages/ConversationModels'));
const BetaLab = lazyWithRetry(() => import('./pages/BetaLab'));
// Marketing Pages
const KnowledgePage = lazyWithRetry(() => import('./pages/KnowledgePage'));
// Inbound Reception Pages (production system)
const ReceptionLaunchpad = lazyWithRetry(() => import('./pages/ReceptionLaunchpad'));
const ReceptionDashboard = lazyWithRetry(() => import('./pages/ReceptionDashboard'));
const InboundActivityLog = lazyWithRetry(() => import('./pages/InboundActivityLog'));
const InboundNumberSetup = lazyWithRetry(() => import('./pages/InboundNumberSetup'));
const InboundVoicemailsPage = lazyWithRetry(() => import('./pages/InboundVoicemailsPage'));
const InboundCallbacksPage = lazyWithRetry(() => import('./pages/InboundCallbacksPage'));
const InboundAnalyticsPage = lazyWithRetry(() => import('./pages/InboundAnalyticsPage'));
const InboundCallRouting = lazyWithRetry(() => import('./pages/InboundCallRouting'));
const InboundBusinessHours = lazyWithRetry(() => import('./pages/InboundBusinessHours'));
// Legacy inbound pages (backward compat)
const InboundOverview = lazyWithRetry(() => import('./pages/InboundOverview'));
const InboundConsole = lazyWithRetry(() => import('./pages/InboundConsole'));
const InboundVoicemail = lazyWithRetry(() => import('./pages/InboundVoicemail'));
const InboundPresence = lazyWithRetry(() => import('./pages/InboundPresence'));
const InboundAnalytics = lazyWithRetry(() => import('./pages/InboundAnalytics'));
const InboundSettings = lazyWithRetry(() => import('./pages/InboundSettings'));
const InboundConfigure = lazyWithRetry(() => import('./pages/InboundConfigure'));

// Infrastructure Pages
const Integrations = lazyWithRetry(() => import('./pages/Integrations'));
const MetaAdsCampaigns = lazyWithRetry(() => import('./pages/MetaAdsCampaigns'));
const GoogleAdsCampaigns = lazyWithRetry(() => import('./pages/GoogleAdsCampaigns'));
const LinkedInAdsCampaigns = lazyWithRetry(() => import('./pages/LinkedInAdsCampaigns'));
const AudienceSearchPreview = import.meta.env.DEV
  ? lazyWithRetry(() => import('./pages/dev/AudienceSearchPreview'))
  : null;
const BriefFitPreview = import.meta.env.DEV
  ? lazyWithRetry(() => import('./pages/dev/BriefFitPreview'))
  : null;
const DevJoinPreview = import.meta.env.DEV
  ? lazyWithRetry(() => import('./pages/DevJoinPreview'))
  : null;
const DevSetupPreview = import.meta.env.DEV
  ? lazyWithRetry(() => import('./pages/DevSetupPreview'))
  : null;
const SignalChoicePreview = import.meta.env.DEV
  ? lazyWithRetry(() => import('./pages/dev/SignalChoicePreview'))
  : null;

// Campaign Details Page (deep dive into single campaign)
const CampaignDetails = lazyWithRetry(() => import('./pages/CampaignDetails'));
// Sequence Overview — new unified one-stop page that replaces Lane View +
// Review-and-Launch (consolidation per migration 075 + family endpoint)
const SequenceOverview = lazyWithRetry(() => import('./pages/SequenceOverview'));
// Transcript Browser Page
const TranscriptBrowser = lazyWithRetry(() => import('./pages/TranscriptBrowser'));
const CallingSettings = lazyWithRetry(() => import('./pages/settings/CallingSettings'));
const LocalPresenceRules = lazyWithRetry(() => import('./pages/LocalPresenceRules'));
const Odin = lazyWithRetry(() => import('./pages/Odin'));
const OdinQuickLaunch = lazyWithRetry(() => import('./pages/OdinQuickLaunch'));
const InboundQuickLaunch = lazyWithRetry(() => import('./pages/InboundQuickLaunch'));
const InboundCampaigns = lazyWithRetry(() => import('./pages/InboundCampaigns'));
const InboundFlowPage = lazyWithRetry(() => import('./pages/InboundFlowPage'));
const InboundBuilderPage = lazyWithRetry(() => import('./pages/InboundBuilderPage'));
const RepliesSettings = lazyWithRetry(() => import('./pages/settings/RepliesSettings'));
const VoiceNotesSettings = lazyWithRetry(() => import('./pages/settings/VoiceNotesSettings'));

function LegacyReplyInboxRedirect() {
  const { search } = useLocation();
  return <Navigate to={`/app/replies${search}`} replace />;
}

// Settings Pages (new centralized settings)
const SettingsLayout = lazyWithRetry(() => import('./layout/SettingsLayout'));
const ProfileSettings = lazyWithRetry(() => import('./pages/settings/ProfileSettings'));
const TeamSettings = lazyWithRetry(() => import('./pages/settings/TeamSettings'));
const AvailabilitySettings = lazyWithRetry(() => import('./pages/settings/AvailabilitySettings'));
const NotificationSettings = lazyWithRetry(() => import('./pages/settings/NotificationSettings'));
const TerritorySettings = lazyWithRetry(() => import('./pages/settings/TerritorySettings'));
const IcpSettings = lazyWithRetry(() => import('./pages/settings/IcpSettings'));
const OdinSettings = lazyWithRetry(() => import('./pages/settings/OdinSettings'));

// Invitation Flow
const InviteAccept = lazyWithRetry(() => import('./pages/InviteAccept'));
const ClaimProvision = lazyWithRetry(() => import('./pages/ClaimProvision'));
const HubSpotGuide = lazyWithRetry(() => import('./pages/HubSpotGuide'));
const SalesforceGuide = lazyWithRetry(() => import('./pages/SalesforceGuide'));

// Enhanced wrapper with error boundary
type RouteLoadingVariant = 'page' | 'table' | 'form' | 'canvas' | 'panel' | 'spinner';

interface LazyWrapperProps {
  children: React.ReactNode;
  /** Match the page's own first loading state so the blocks do not swap. */
  variant?: RouteLoadingVariant;
  /**
   * Full-viewport bone fill for top-level routes. Off inside a layout
   * that is already on screen (settings), so the fallback stays in the outlet.
   */
  fill?: boolean;
  /** Extra classes on the skeleton, for a route whose fallback must stay inset. */
  className?: string;
}

const LazyWrapper = ({ children, variant = 'page', fill = true, className = '' }: LazyWrapperProps) => (
  <Suspense
    fallback={
      variant === 'spinner' ? (
        <BootSpinner />
      ) : fill ? (
        // min-h-screen covers public routes that have no height chain, so
        // the bone page does not leave a second background below the blocks.
        <div className="flex-1 w-full min-h-screen min-w-0 overflow-x-hidden bg-bone">
          <PageLoading variant={variant} className={className} />
        </div>
      ) : (
        <PageLoading variant={variant} className={className} />
      )
    }
  >
    {children}
  </Suspense>
);

export const router = createBrowserRouter([
  {
    path: '/integrations/hubspot',
    element: (
      <LazyWrapper>
        <HubSpotGuide />
      </LazyWrapper>
    ),
  },
  {
    path: '/integrations/salesforce',
    element: (
      <LazyWrapper>
        <SalesforceGuide />
      </LazyWrapper>
    ),
  },
  ...(import.meta.env.DEV && AudienceSearchPreview
    ? [{
        path: '/dev/audience-search',
        element: (
          <LazyWrapper>
            <AudienceSearchPreview />
          </LazyWrapper>
        ),
      }]
    : []),
  ...(import.meta.env.DEV && DevJoinPreview
    ? [{
        path: '/dev/join',
        element: (
          <LazyWrapper>
            <DevJoinPreview />
          </LazyWrapper>
        ),
      }]
    : []),
  ...(import.meta.env.DEV && DevSetupPreview
    ? [{
        path: '/dev/setup',
        element: (
          <LazyWrapper>
            <DevSetupPreview />
          </LazyWrapper>
        ),
      }]
    : []),
  ...(import.meta.env.DEV && BriefFitPreview
    ? [{
        path: '/dev/brief-fit',
        element: (
          <LazyWrapper>
            <BriefFitPreview />
          </LazyWrapper>
        ),
      }]
    : []),
  ...(import.meta.env.DEV && SignalChoicePreview
    ? [{
        path: '/dev/signal-choice',
        element: (
          <LazyWrapper>
            <SignalChoicePreview />
          </LazyWrapper>
        ),
      }]
    : []),
  {
    path: '/',
    element: (
      <LazyWrapper>
        <Hero />
      </LazyWrapper>
    ),
  },
  {
    path: '/login',
    element: (
      <LazyWrapper>
        <Login />
      </LazyWrapper>
    ),
  },
  {
    path: '/app/connect-done',
    element: <ConnectDone />,
  },
  {
    // Legacy path: Launchpad gates first-time users to Odin when needed.
    path: '/onboarding',
    element: <Navigate to="/app" replace />,
  },
  {
    path: '/signup',
    element: (
      <LazyWrapper>
        <SignUp />
      </LazyWrapper>
    ),
  },
  // Team Invitation Acceptance
  {
    path: '/claim/:token',
    element: (
      <LazyWrapper>
        <ClaimProvision />
      </LazyWrapper>
    ),
  },
  {
    path: '/invite/:token',
    element: (
      <LazyWrapper>
        <InviteAccept />
      </LazyWrapper>
    ),
  },
  {
    path: '/legal/privacy',
    element: (
      <LazyWrapper>
        <LegalPrivacy />
      </LazyWrapper>
    ),
  },
  {
    path: '/legal/terms',
    element: (
      <LazyWrapper>
        <LegalTerms />
      </LazyWrapper>
    ),
  },
  {
    path: '/legal/dpa',
    element: (
      <LazyWrapper>
        <LegalDPA />
      </LazyWrapper>
    ),
  },
  {
    path: '/legal/subprocessors',
    element: (
      <LazyWrapper>
        <LegalSubprocessors />
      </LazyWrapper>
    ),
  },
  {
    path: '/legal/cookies',
    element: (
      <LazyWrapper>
        <LegalCookies />
      </LazyWrapper>
    ),
  },
  {
    path: '/unsubscribe',
    element: (
      <LazyWrapper>
        <UnsubscribeBridge />
      </LazyWrapper>
    ),
  },
  // Documentation Pages
  {
    path: '/docs',
    element: (
      <LazyWrapper>
        <DocsHub />
      </LazyWrapper>
    ),
  },
  {
    path: '/docs/consent',
    element: (
      <LazyWrapper>
        <DocsConsent />
      </LazyWrapper>
    ),
  },
  {
    path: '/conversation-models',
    element: (
      <LazyWrapper>
        <ConversationModels />
      </LazyWrapper>
    ),
  },
  {
    path: '/beta-lab',
    element: (
      <LazyWrapper>
        <BetaLab />
      </LazyWrapper>
    ),
  },
  {
    path: '/how-agents-remember',
    element: (
      <LazyWrapper>
        <KnowledgePage />
      </LazyWrapper>
    ),
  },
  {
    path: '/app',
    element: (
      <ProtectedRoute>
        <AppShell />
      </ProtectedRoute>
    ),
    children: [
      {
        index: true,
        element: (
          <LazyWrapper>
            <WorkspaceLaunchpad />
          </LazyWrapper>
        ),
      },
      {
        path: 'odin',
        element: (
          <LazyWrapper variant="spinner">
            <Odin />
          </LazyWrapper>
        ),
      },
      {
        path: 'odin/quick',
        element: (
          <LazyWrapper variant="spinner">
            <OdinQuickLaunch />
          </LazyWrapper>
        ),
      },
      // Settings Hub (new centralized layout)
      {
        path: 'settings',
        element: (
          <LazyWrapper>
            <SettingsLayout />
          </LazyWrapper>
        ),
        children: [
          {
            index: true,
            element: <Navigate to="profile" replace />,
          },
          {
            path: 'profile',
            element: (
              <LazyWrapper fill={false}>
                <ProfileSettings />
              </LazyWrapper>
            ),
          },
          {
            path: 'team',
            element: (
              <LazyWrapper fill={false}>
                <TeamSettings />
              </LazyWrapper>
            ),
          },
          {
            path: 'availability',
            element: (
              <LazyWrapper variant="form" fill={false}>
                <AvailabilitySettings />
              </LazyWrapper>
            ),
          },
          {
            path: 'calling',
            element: (
              <LazyWrapper variant="form" fill={false}>
                <CallingSettings />
              </LazyWrapper>
            ),
          },
          {
            path: 'integrations',
            element: (
              <LazyWrapper fill={false}>
                <Integrations />
              </LazyWrapper>
            ),
          },
          {
            path: 'linkedin',
            element: (
              <LazyWrapper fill={false}>
                <LinkedInSettings />
              </LazyWrapper>
            ),
          },
          {
            path: 'notifications',
            element: (
              <LazyWrapper variant="form" fill={false}>
                <NotificationSettings />
              </LazyWrapper>
            ),
          },
          {
            path: 'replies',
            element: (
              <LazyWrapper variant="form" fill={false}>
                <RepliesSettings />
              </LazyWrapper>
            ),
          },
          ...(VOICE_NOTES_ENABLED
            ? [
                {
                  path: 'voice-notes',
                  element: (
                    <LazyWrapper variant="form" fill={false}>
                      <VoiceNotesSettings />
                    </LazyWrapper>
                  ),
                },
              ]
            : []),
          {
            path: 'territories',
            element: (
              <LazyWrapper variant="form" fill={false}>
                <TerritorySettings />
              </LazyWrapper>
            ),
          },
          {
            path: 'icp',
            element: (
              <LazyWrapper variant="form" fill={false}>
                <IcpSettings />
              </LazyWrapper>
            ),
          },
          {
            path: 'odin',
            element: (
              <LazyWrapper variant="form" fill={false}>
                <OdinSettings />
              </LazyWrapper>
            ),
          },
          {
            path: 'local-presence',
            element: (
              <LazyWrapper fill={false}>
                <LocalPresenceRules />
              </LazyWrapper>
            ),
          },
        ],
      },
      {
        path: 'home-chat',
        element: (
          <LazyWrapper>
            <HomeChat />
          </LazyWrapper>
        ),
      },
      // ============================================================
      // INBOUND RECEPTION — Production Routes
      // ============================================================
      {
        path: 'inbound/launchpad',
        element: <Navigate to="/app" replace />,
      },
      {
        path: 'inbound/dashboard',
        element: (
          <LazyWrapper>
            <ReceptionDashboard />
          </LazyWrapper>
        ),
      },
      {
        path: 'inbound/activity',
        element: (
          <LazyWrapper>
            <InboundActivityLog />
          </LazyWrapper>
        ),
      },
      {
        path: 'inbound/voicemails',
        element: <Navigate to="/app/inbound/activity" replace />,
      },
      {
        path: 'inbound/callbacks',
        element: <Navigate to="/app/inbound/activity" replace />,
      },
      {
        path: 'inbound/analytics',
        element: <Navigate to="/app/inbound/dashboard" replace />,
      },
      {
        path: 'inbound/configure',
        element: <Navigate to="/app/settings/calling" replace />,
      },
      {
        path: 'inbound/routing',
        element: <Navigate to="/app/inbound/dashboard" replace />,
      },
      {
        path: 'inbound/hours',
        element: <Navigate to="/app/inbound/dashboard" replace />,
      },
      // Legacy inbound redirects (old routes → new production routes)
      {
        path: 'inbound/overview',
        element: <Navigate to="/app/inbound/dashboard" replace />,
      },
      {
        path: 'inbound/console',
        element: <Navigate to="/app/inbound/dashboard" replace />,
      },
      {
        path: 'inbound/voicemail',
        element: <Navigate to="/app/inbound/voicemails" replace />,
      },
      {
        path: 'inbound/agent-presence',
        element: <Navigate to="/app/inbound/dashboard" replace />,
      },
      {
        path: 'inbound/settings',
        element: <Navigate to="/app/inbound/configure" replace />,
      },
      {
        path: 'inbound/reception',
        element: <Navigate to="/app/inbound/dashboard" replace />,
      },
      {
        path: 'inbound/number-setup',
        element: <Navigate to="/app/inbound/configure" replace />,
      },
      // AI Calling Campaigns removed - redirect to Quick Call (unified voice tool)
      {
        path: 'ai-calling',
        element: <Navigate to="/app/outbound/quick-call" replace />,
      },
      // Dashboard removed - redirect to Launchpad (home)
      {
        path: 'dashboard',
        element: <Navigate to="/app" replace />,
      },
      {
        path: 'contacts',
        element: (
          <LazyWrapper>
            <Contacts />
          </LazyWrapper>
        ),
      },
      // Redirect old sequences route to campaigns
      {
        path: 'sequences',
        element: <Navigate to="/app/outbound/campaigns" replace />,
      },
      // New Outbound Routes
      // Outbound AI Calling removed - redirect to Quick Call
      {
        path: 'outbound/ai-calling',
        element: <Navigate to="/app/outbound/quick-call" replace />,
      },
      {
        path: 'outbound/quick-call',
        element: (
          <LazyWrapper>
            <QuickCall />
          </LazyWrapper>
        ),
      },
      // CAMPAIGNS - Unified campaign builder (replaces email-campaign and sequences)
      {
        path: 'outbound/campaigns',
        element: (
          <LazyWrapper>
            <Campaigns />
          </LazyWrapper>
        ),
      },
      // SequenceOverview - one-stop campaign page (plan/launch + performance).
      // Replaces the old Lane View / Review-and-Launch duplication for both
      // Odin Autopilot families and single manual campaigns.
      {
        path: 'outbound/campaigns/:id',
        element: (
          <LazyWrapper>
            <SequenceOverview />
          </LazyWrapper>
        ),
      },
      // Redirects from old routes to new campaigns route
      {
        path: 'outbound/email-campaign',
        element: <Navigate to="/app/outbound/campaigns" replace />,
      },
      {
        path: 'outbound/sequences',
        element: <Navigate to="/app/outbound/campaigns" replace />,
      },
      ...(INBOUND_FLOWS_ENABLED
        ? [
            {
              path: 'inbound',
              element: (
                <LazyWrapper>
                  <InboundCampaigns />
                </LazyWrapper>
              ),
            },
            {
              path: 'inbound/new',
              element: (
                <LazyWrapper variant="spinner">
                  <InboundQuickLaunch />
                </LazyWrapper>
              ),
            },
            {
              path: 'inbound/:id',
              element: (
                <LazyWrapper>
                  <InboundFlowPage />
                </LazyWrapper>
              ),
            },
            {
              path: 'inbound/:id/builder',
              element: (
                <LazyWrapper variant="spinner">
                  <InboundBuilderPage />
                </LazyWrapper>
              ),
            },
          ]
        : []),
      {
        path: 'replies',
        element: (
          <LazyWrapper className="mx-auto w-full max-w-4xl">
            <ReplyInbox />
          </LazyWrapper>
        ),
      },
      // Reply Inbox used to live under Outbound. Keep the old address.
      {
        path: 'outbound/reply-inbox',
        element: <LegacyReplyInboxRedirect />,
      },
      // Email Templates - available under outbound
      {
        path: 'outbound/templates',
        element: (
          <LazyWrapper>
            <EmailTemplates />
          </LazyWrapper>
        ),
      },
      // LinkedIn Settings - Redirect to settings section
      {
        path: 'outbound/linkedin',
        element: <Navigate to="/app/settings/linkedin" replace />,
      },
      {
        path: 'phone-numbers',
        element: (
          <LazyWrapper>
            <PhoneNumbers />
          </LazyWrapper>
        ),
      },
      {
        path: 'lead-data',
        element: (
          <LazyWrapper>
            <LeadData />
          </LazyWrapper>
        ),
      },
      {
        path: 'integrations',
        element: (
          <LazyWrapper>
            <Integrations />
          </LazyWrapper>
        ),
      },
      {
        path: 'meta-ads',
        element: (
          <LazyWrapper>
            <MetaAdsCampaigns />
          </LazyWrapper>
        ),
      },
      {
        path: 'google-ads',
        element: (
          <LazyWrapper>
            <GoogleAdsCampaigns />
          </LazyWrapper>
        ),
      },
      {
        path: 'linkedin-ads',
        element: (
          <LazyWrapper>
            <LinkedInAdsCampaigns />
          </LazyWrapper>
        ),
      },
      {
        path: 'appointments',
        element: (
          <LazyWrapper>
            <Appointments />
          </LazyWrapper>
        ),
      },
      // Deprecated HubSpot-era CRM / Pipeline / ATS — sidebar-hidden; block URL deep links too
      {
        path: 'ats',
        element: <Navigate to="/app" replace />,
      },
      {
        path: 'pipeline',
        element: <Navigate to="/app" replace />,
      },
      {
        path: 'crm',
        element: <Navigate to="/app" replace />,
      },
      {
        path: 'signals',
        element: (
          <LazyWrapper>
            <Signals />
          </LazyWrapper>
        ),
      },
      // Redirect old email route to campaigns
      {
        path: 'email',
        element: <Navigate to="/app/outbound/campaigns" replace />,
      },
      {
        path: 'email-templates',
        element: (
          <LazyWrapper>
            <EmailTemplates />
          </LazyWrapper>
        ),
      },
      // Redirect old email/reply-inbox to new location
      {
        path: 'email/reply-inbox',
        element: <Navigate to="/app/replies" replace />,
      },
      {
        path: 'email-accounts',
        element: (
          <LazyWrapper>
            <SendingInfrastructure />
          </LazyWrapper>
        ),
      },
      // Deprecated standalone AI Agents UI — configure via Settings → Calling
      {
        path: 'ai-templates',
        element: <Navigate to="/app/settings/calling" replace />,
      },
      {
        path: 'agents',
        element: <Navigate to="/app/settings/calling" replace />,
      },
      {
        path: 'agents/:agentId',
        element: <Navigate to="/app/settings/calling" replace />,
      },
      {
        path: 'sms-templates',
        element: (
          <LazyWrapper>
            <SMSTemplates />
          </LazyWrapper>
        ),
      },
      {
        path: 'admin',
        element: (
          <LazyWrapper>
            <Admin />
          </LazyWrapper>
        ),
      },
      {
        path: 'billing',
        element: (
          <LazyWrapper>
            <Billing />
          </LazyWrapper>
        ),
      },
      {
        path: 'billing/success',
        element: (
          <LazyWrapper>
            <BillingSuccess />
          </LazyWrapper>
        ),
      },
      {
        path: 'setup',
        element: (
          <LazyWrapper>
            <PaidSetup />
          </LazyWrapper>
        ),
      },
      {
        path: 'billing/cancel',
        element: (
          <LazyWrapper>
            <BillingCancel />
          </LazyWrapper>
        ),
      },
      {
        path: 'knowledge',
        element: (
          <LazyWrapper>
            <Knowledge />
          </LazyWrapper>
        ),
      },
      // Campaign Analytics - redirect list view to All Campaigns, keep details route
      {
        path: 'campaign-analytics',
        element: <Navigate to="/app/outbound/campaigns" replace />,
      },
      {
        path: 'campaign-analytics/:campaignId',
        element: (
          <LazyWrapper>
            <CampaignDetails />
          </LazyWrapper>
        ),
      },
      {
        path: 'transcripts',
        element: (
          <LazyWrapper>
            <TranscriptBrowser />
          </LazyWrapper>
        ),
      },
    ],
  },
]); 